/**
 * Created by liuyang on 17/1/12.
 */
import MemoryCache from './Storage/MemoryCache';
import SessionStorageCache from './Storage/SessionStorageCache';
import StorageCache from './Storage/StorageCache';

import {
	isString,
	isNumber,
	isFunction,
  isUndefined,
	extend,
	some,
	get
} from 'lodash';

const isPromiseLike = function (pro) {
	return isFunction(get(pro, 'then')) && isFunction(get(pro, 'catch'));
};
const now = function () {
	return Date.parse(new Date())/1000;
};

class Storage {

	static drives = {
		'memory': MemoryCache,
		'session': SessionStorageCache,
		'storage': StorageCache
	};

	static config = Object.freeze({
		channel: '',
	});

	static cached = {
		'memory': new MemoryCache({
			channel: 'default',
			keyName: 'default!|!__cached_calculate_time__'
		}),
		'session': new SessionStorageCache({
      channel: 'default',
      keyName: 'default!|!__cached_calculate_time__'
		}),
		'storage': new StorageCache({
      channel: 'default',
      keyName: 'default!|!__cached_calculate_time__'
		})
	};

	instance = null;

	drive = null;

	cachedStatistics = null;

	sync = true;

  /**
	 *
   * @param {string} drive=MemoryCache
   * @param {object} config
	 * @param {string} config.channel
	 * @param {string} config.keyName
	 * @param {string} config.validity like 1d,1y,1h,1m 或 3600 单纯数字表示秒
   */
	constructor(drive, config, sync = true) {
		if( !isString(drive) ) {
      config = drive;
      drive = 'memory';
		}
		if (!(drive in Storage.drives) ) {
			throw new Error(`drive ${drive} not found`);
		}
		if ( !config.channel ) {
			config.channel = 'default';
		}
		if ( !config.keyName ) {
			throw new Error('keyName can not be empty!');
		}

		config.validity = Storage.buildExpireTime(config.validity);

		// 驱动信息
		this.drive = drive;

		// 所有缓存走同步队列
		this.sync = sync;

		// 缓存分析信息
		this.cachedStatistics = Storage.cached[drive];

		// 缓存实例
		this.instance = new Storage.drives[drive](extend(config, {
			keyName: config.channel + '!|!' + config.keyName
		}));
	}
	/**
	 * 获取当前storage中存储的数据
	 * @param keyName 需要存储的key的名称，例如：'a.b.c', 'a.0.2'
	 * @param callback 设置成功之后的回调函数 以(error, data) => {}方式
	 * @param def 如果获取到的数据为空，则直接用def作为默认值传入
	 * @returns {Promise.<*>}
	 */
	get(key, callback, def) {
		let self = this;
    let instance = self.instance;
    let checkResult = self.check();
    let checkIsExpire = function (result) {
      let {
        isExpired
      } = result;

      if ( isExpired ) {
      	let runClear = instance.clear();
      	let runClearCallback = function () {
      		let ret = isUndefined(def) ? null : def;
      		return isFunction(callback) ? callback(ret) : ret;
        };
      	if(isPromiseLike(runClear)) {
      		return runClear.then(runClearCallback)
				} else {
      		return runClearCallback(runClear);
				}
      }
      let ret = instance.get(key, callback, def);
			if (self.sync || isPromiseLike(ret)) {
				return ret;
			}
			return Promise.resolve(ret);
    };

    if ( isPromiseLike(checkResult) ) {
    	return checkResult.then(checkIsExpire);
		} else {
    	let ret = checkIsExpire(checkResult);
    	if ( self.sync || isPromiseLike(ret)) {
    		return ret;
			}
      return Promise.resolve(ret);
    }
	}
	/**
	 * 把当前数据设置到Storage中去
	 * @param key 要设置的名称，支持以点连接的方式
	 * @param value 需要设置的值
	 * @param callback 设置成功之后的回调
	 * @returns {Promise.<*>}
	 */
	set(key, value, callback) {
		let self = this;
		let instance = self.instance;
		let config = instance.config();
		let [channel, keyName] = config.keyName.split('!|!');
		let cachedKeyName = channel + '.' + keyName;
		let cachedInstance = Storage.cached[this.drive];

		let checkResult = this.check();

		let resolveFn = function(result) {
      let nowTime = now();
      let {
        isExpired,
        createdAt,
        updatedAt,
        expiredAt
      } = result;

      if ( isExpired ) {
        createdAt = nowTime;
        updatedAt = nowTime;
        expiredAt = nowTime + config.validity;

        let runResult = [
        	instance.clear(),
          instance.set(key, value, callback),
          cachedInstance.set(cachedKeyName, {
            createdAt,
            updatedAt,
            expiredAt
          })
				];
				if(some(runResult, isPromiseLike)) {
					return Promise.all(runResult).then((args)=>args[1]);
				}

				if(self.sync) {
					return runResult[1];
				} else if(isPromiseLike(runResult[1])) {
					return runResult[1];
				} else {
					return Promise.resolve(runResult[1]);
				}
      }
      let runResult = [cachedInstance.set(cachedKeyName + '.updatedAt', nowTime), instance.set(key, value, callback)];

      if (self.sync) {
      	return runResult[1];
			} else if(some(runResult, isPromiseLike)) {
      	return Promise.all(runResult).then((args)=>args[1]);
			} else {
				return Promise.resolve(runResult[1]);
			}
    };

		if ( isPromiseLike(checkResult) ) {
      return checkResult.then(resolveFn);
		}
		let returnResult = resolveFn(checkResult);

		if (this.sync || isPromiseLike(returnResult)) {
			return returnResult;
		} else {
			return Promise.resolve(returnResult);
		}
  }
	/**
	 * 删除当前store中的数据值
	 * @param keyName
	 * @param callback
	 * @returns {Promise}
	 */
	clear(keyName, callback) {
		let result = this.instance.clear(keyName, callback);
		if ( this.sync || isPromiseLike(result)) {
			return result;
		} else {
			return Promise.resolve(result);
		}
	}

  /**
	 * 检测当前实例是否过期
   */
	check(){
    let instance = this.instance;
    let config = instance.config();
    let [channel, keyName] = config.keyName.split('!|!');
    let cachedKeyName = channel + '.' + keyName;
    let cachedInstance = Storage.cached[this.drive];

    let getResult = cachedInstance.get( cachedKeyName );

    let checkResultFn = function(result) {
      let nowTime = now();
      let {
        expiredAt = nowTime - 1,
        updatedAt = nowTime - 1,
        createdAt = nowTime - 1
      } = (result || {});

      return {
        isExpired: nowTime > expiredAt,
        expiredAt,
        updatedAt,
        createdAt
      };
    };

    let clearCacheFn = function(result) {
      // 已过期
      if (result.isExpired === true) {
      	let clearResult = cachedInstance.clear(cachedKeyName);
      	if( isPromiseLike(clearResult) ) {
          return clearResult.then(() => {
            return result;
          });
				}
      }
      return result;
    };

    if ( isPromiseLike(getResult) ) {
      return getResult.
      then(checkResultFn).then(clearCacheFn);
		}

    let result = clearCacheFn(checkResultFn(getResult));
    if ( this.sync || isPromiseLike(result)) {
    	return result;
		} else {
    	return Promise.resolve(result);
		}
	}

  /**
	 *
   * @param value
   */
	merge(value) {

	}

  /**
   * 把当前对象的值转换为JSON字符串
   * @return {string}
   */
  toJSON() {
    return this.instance.toJSON();
  }

	/**
	 *
	 */
	static getAllKey(callback) {
		if (typeof callback === 'function') {
			return;
		}
	}
	/**
	 * 输入过期时间，生成对应的秒数
	 * @param {String} expired string {1d,1y,1h,1m}      //1天，1年，1小时，1分钟
	 * @returns {number}
	 */
	static buildExpireTime(expired) {
		const map = {
			d: 86400,
			y: 31536000,
			h: 3600,
			m: 60,
		};
		let parsed;
		if (isNumber(expired)) {
      return expired;
		}
		if (parsed = expired.toString().match(/^([0-9]+(?:.[0-9]{2})?)([dymh])$/i)) {
			return parsed[1] * (map[parsed[2]] || 86400);
		}
		return 1;
	}
}


export default Storage;


// WEBPACK FOOTER //
// ./src/class/Storage.js